import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Method, TaskStatus, TaskType } from '@/modules/tasks/taskboard/kanban/constants';
import { useIntl } from 'react-intl';
import { getRelationName } from '@/shared/components/RelationName';
import SignalRService from '../services/SignalRService';
import { useDataContext } from './DataProvider';
import { getHoursMinutesFromTime, getTime } from '../utils/dateHelper';
import TimerRegistrationService from '../services/TimerRegistrationService';
import { useGetRunningTaskId } from '../hooks/task/useGetTimerRegistration';
import { formatListValue } from '../components/FormattedListValue';
import ToastrService from '../services/ToastrService';
import messages from '../messages/appMessages';
import { ModalType, useModalContext } from './ModalProvider';
import HandleHttpError from '../services/HandleHttpError';
export const TaskTimerContext = React.createContext(null);
export const useTaskTimerContext = () => {
    const ctx = useContext(TaskTimerContext);
    if (!ctx) {
        throw new Error('Wrap app with TaskTimerProvider');
    }
    return ctx;
};
const TaskTimerProvider = ({ children }) => {
    const intl = useIntl();
    const { setShowModalInfo } = useModalContext();
    const { defaultCompany } = useDataContext();
    const isTimerRunningForSubTaskId = useGetRunningTaskId();
    // const { updateTaskStatus } = useUpdateTaskStatus();
    const tasks = useRef([]);
    const processTimeRegistrationResult = useCallback(async (timeRegistrationResult, isAffectedRegistration = false) => {
        var _a;
        if (!timeRegistrationResult)
            return;
        const { registration } = timeRegistrationResult;
        let taskName = null;
        if (registration === null || registration === void 0 ? void 0 : registration.plannedTaskId) {
            taskName =
                (_a = registration.plannedTaskName) !== null && _a !== void 0 ? _a : formatListValue({
                    list: 'tasktemplate',
                    id: registration.plannedTaskTemplateId,
                    intl,
                    fallback: registration.plannedTaskName,
                });
        }
        if (timeRegistrationResult.isRunning) {
            ToastrService.success(taskName
                ? intl.formatMessage(messages.msgTaskTimeRegistrationStarted, { task: taskName })
                : intl.formatMessage(messages.msgTimerRegistrationStarted));
        }
        else if (timeRegistrationResult.discarded) {
            ToastrService.info(intl.formatMessage(messages.msgTaskTimeRegistrationDiscarded));
        }
        else {
            const duration = getHoursMinutesFromTime(registration.durationMinutes);
            ToastrService.success(intl.formatMessage(messages.msgTaskTimeRegistrationCreated, {
                duration,
                relation: getRelationName(intl, registration.relation),
            }), undefined, () => {
                const task = {
                    relation: registration === null || registration === void 0 ? void 0 : registration.relation,
                    mainTaskId: registration === null || registration === void 0 ? void 0 : registration.mainPlannedTaskId,
                    mainTemplateId: registration === null || registration === void 0 ? void 0 : registration.mainTaskTemplateId,
                };
                setShowModalInfo({
                    modalType: ModalType.TimeRegistration,
                    task,
                    registration,
                });
            });
        }
        if (timeRegistrationResult.affectedRegistrations &&
            timeRegistrationResult.affectedRegistrations.length > 0) {
            timeRegistrationResult.affectedRegistrations.forEach(affectedRegistration => {
                processTimeRegistrationResult(affectedRegistration, true);
            });
        }
    }, [intl, setShowModalInfo]);
    const taskTimeRegistrationUpdatedHandler = useCallback((taskTimeRegistrationResult) => {
        var _a;
        const subTask = (_a = tasks.current) === null || _a === void 0 ? void 0 : _a.find(x => x.id === taskTimeRegistrationResult.plannedTaskId);
        if (!subTask)
            return;
        processTimeRegistrationResult(taskTimeRegistrationResult);
    }, [processTimeRegistrationResult, tasks]);
    useEffect(() => {
        SignalRService.subscribe('taskTimeRegistrationUpdated', taskTimeRegistrationUpdatedHandler);
        return () => SignalRService.unsubscribe('taskTimeRegistrationUpdated', taskTimeRegistrationUpdatedHandler);
    }, [taskTimeRegistrationUpdatedHandler]);
    const stopTimer = useCallback(async (pause = false) => {
        try {
            const timeRegistrationResult = await TimerRegistrationService.stopTimerRegistration(pause);
            await processTimeRegistrationResult(timeRegistrationResult);
        }
        catch (e) {
            HandleHttpError(intl)(e);
        }
    }, [intl, processTimeRegistrationResult]);
    // Manuel click on button on subtask
    const startStopTimer = useCallback(async (t, subTask) => {
        subTask.disabled = true;
        const runningRegistration = TimerRegistrationService.getRunningRegistration();
        const timerTaskId = runningRegistration === null || runningRegistration === void 0 ? void 0 : runningRegistration.plannedTaskId;
        if (subTask.id === timerTaskId) {
            // Stop timer
            stopTimer(true);
            subTask.disabled = false;
        }
        else {
            if (!subTask.resourceId) {
                // Maybe open modal instead?
                ToastrService.error(intl.formatMessage(messages.msgNoResourceDefined));
                return;
            }
            if (subTask.isBlocked) {
                // await updateTaskStatus({
                //   id: subTask.id,
                //   status: subTask.status,
                //   isBlocked: !subTask.isBlocked,
                //   inapplicable: subTask.inapplicable,
                // });
            }
            const mainTask = t;
            const createStartTaskTimeRegistrationValues = {
                invoiceStreamId: subTask.invoiceStreamId,
                resourceId: subTask.resourceId,
                plannedTaskId: subTask.id,
                fromMinutes: getTime(),
                plannedTaskTemplateId: subTask.templateId,
                plannedTaskName: subTask.name,
                mainPlannedTaskId: mainTask.id,
                mainTaskTemplateId: mainTask.templateId,
                mainPlannedTaskName: mainTask.name,
                referenceYear: subTask.year,
            };
            try {
                const timeRegistrationResult = await TimerRegistrationService.createTimerRegistration(createStartTaskTimeRegistrationValues);
                await processTimeRegistrationResult(timeRegistrationResult);
            }
            catch (e) {
                HandleHttpError(intl)(e);
            }
            subTask.disabled = false;
        }
    }, [intl, stopTimer, processTimeRegistrationResult]);
    // Status change after dragging or manual
    const startStopTimerAfterStatusChange = useCallback(async (t) => {
        if (defaultCompany.taskTimeRegistrationMethod !== Method.AutomaticRegistration)
            return;
        const runningRegistration = TimerRegistrationService.getRunningRegistration();
        const timerTaskId = runningRegistration === null || runningRegistration === void 0 ? void 0 : runningRegistration.plannedTaskId;
        if (!t.parentId) {
            // Main task
            if (t.status !== TaskStatus.Done)
                return;
            // If the main task is done, check if there where subtasks with running timers and stop them
            const subTasksWithTimer = t.children.filter(x => {
                if (x.type === TaskType.DefaultTime ||
                    x.type === TaskType.Mail ||
                    x.type === TaskType.AutomaticMail) {
                    return false;
                }
                return x.status === TaskStatus.Done && x.id === timerTaskId;
            });
            if (subTasksWithTimer.length > 0) {
                await stopTimer();
            }
            return;
        }
        // Subtask
        const taskType = t.type;
        if (taskType === TaskType.DefaultTime ||
            taskType === TaskType.Mail ||
            taskType === TaskType.AutomaticMail)
            return;
        if (t.status === TaskStatus.InProgress) {
            // Start new timer
            const mainTask = tasks.current.find(x => x.id === t.parentId);
            await startStopTimer(mainTask, t);
            return;
        }
        if (t.id !== timerTaskId)
            return;
        // Moved from InProgress to another status
        await stopTimer();
    }, [defaultCompany.taskTimeRegistrationMethod, tasks, stopTimer, startStopTimer]);
    const onSetTasks = useCallback(async (allTasks) => {
        var _a;
        const tsks = (_a = allTasks === null || allTasks === void 0 ? void 0 : allTasks.reduce((prev, t) => { var _a; return [...prev, ...((_a = t.children) !== null && _a !== void 0 ? _a : []), t]; }, [])) !== null && _a !== void 0 ? _a : [];
        tasks.current = tsks;
    }, []);
    const value = useMemo(() => ({
        processTimeRegistrationResult,
        startStopTimerAfterStatusChange,
        startStopTimer,
        setTasks: onSetTasks,
        isTimerRunningForSubTaskId,
    }), [
        processTimeRegistrationResult,
        startStopTimerAfterStatusChange,
        startStopTimer,
        onSetTasks,
        isTimerRunningForSubTaskId,
    ]);
    return _jsx(TaskTimerContext.Provider, Object.assign({ value: value }, { children: children }));
};
export default TaskTimerProvider;
